import { AODA } from "@core/utilities/aoda";
import {
  Component,
  ElementRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { BaseComponent } from "@shared/components/base.component";
import { FormGroup } from "@angular/forms";
import { startWith, takeWhile, switchMap, exhaustMap, tap, filter } from "rxjs/operators";
import { ElasticsearchQueryService } from "@shared/services/elasticsearch-query.service";
import { Utils } from "@core/utilities/utils";
import { Observable, of, Subject } from "rxjs";

@Component({
  selector: "app-author-filter",
  templateUrl: "./author-filter.component.html",
  styleUrls: ["./author-filter.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class AuthorFilter extends BaseComponent implements OnInit, OnDestroy {
  @Input() f: FormGroup;
  @Input() clearFilter: boolean;
  @Input() enableFilter: boolean;
  displayAuthorFilter: boolean = false;
  public height: string;
  totalFilterValues: number = 0;

  //Auto-Complete Variables
  lastViewedPage: number;
  lastFilter = "";
  authors: any[] = [];
  selectedAuthors: any[] = new Array<any>();
  filteredAuthors: any[];
  isAllSelected = false;
  isInitialized = false;
  filteredAuthors$: Observable<any[]> | undefined;
  private nextPage$ = new Subject();

  constructor(
    private injector: Injector,
    private el: ElementRef,
    private r2: Renderer2,
    private searchFiltersService: ElasticsearchQueryService
  ) {
    super(injector);
  }

  ngOnInit() {
    super.ngOnInit();

    if(this.f.get("author").value.length > 0) {
      this.displayAuthorFilter = true;
      this.makeAuthorRequest();
    } else {
      this.displayAuthorFilter = false;
    }

    this.selectedAuthors = this.f.get("author").value;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    AODA.applyAllFixes(this.el, this.r2, this.languageService.languageType);
  }

  ngOnChanges() {
    //Reset the last viewed page if click outside Author filter area
    this.r2.listen('window', 'click',(e:Event)=>{
      this.lastViewedPage = 1;
      this.tempAuthor.setValue("");
      this.getObservableValues();
    });

    if (this.clearFilter) {
      this.unselectAll();
      this.clearFilter = false;
    }

    if (!this.enableFilter) {
      this.displayAuthorFilter = false;
    }
  }

  getAuthorList(startsWith: any, page: number): Observable<any[]> {
    const take = 10;
    const skip = page > 0 ? (page - 1) * take : 0;
    const filtered = this.authors.filter((option) =>
      option._source.name.toLowerCase().startsWith(startsWith.toLowerCase())
    );
    this.lastViewedPage = page;
    return of(filtered.slice(0, skip + take));
  }

  displayWith(element: any) {
    return element ? element.name : null;
  }

  onScroll() {
    this.nextPage$.next(null);
  }

  //Filter Functions
  onDisplayAuthorFilter() {
    if (this.enableFilter) {
      if (!this.displayAuthorFilter && !this.isInitialized) {
        this.makeAuthorRequest();
      }
      this.displayAuthorFilter = !this.displayAuthorFilter;
    }
  }

  makeAuthorRequest() {
    this.isInitialized = true;
    this.searchFiltersService
      .getAuthors()
      .pipe(takeWhile(() => this.alive))
      .subscribe((authors) => {
        this.totalFilterValues = authors.hits.hits.length;
        this.authors = this.filteredAuthors =
          Utils.sortElasticSearchFilterList(authors.hits.hits, "name");
        this.getObservableValues();
      });
  }

  getObservableValues() {
    const filter$ = this.tempAuthor.valueChanges.pipe(
      startWith(""),
      filter((q) => typeof q === "string")
    );

    this.filteredAuthors$ = filter$.pipe(
      switchMap((filter) => {
        let currentPage = this.lastViewedPage;
        return this.nextPage$.pipe(
          startWith(currentPage),
          exhaustMap((_) => this.getAuthorList(filter, currentPage)),
          tap(() => currentPage++)
        );
      })
    );
  }
  //Filter Functions

  //Auto-Complete
  isAuthorChecked(item: any) {
    if (this.isSelected(item)) {
      item.selected = true;
      return true;
    }
    item.selected = false;
    return false;
  }

  isSelected(item: any): boolean {
    return this.author.value.includes(item._source.name);
  }

  updateAllSelected() {
    return (this.isAllSelected =
      this.author.value.length == this.totalFilterValues ? true : false);
  }

  toggleSelectAll(event: any) {
    //Needed for mat-option and mat-checkbox
    if (event.isUserInput || event.source._currentCheckState) {
      this.isAllSelected = !this.isAllSelected;
      this.selectedAuthors = [];

      for (let i = 0; i < this.authors.length; i++) {
        this.authors[i].selected = this.isAllSelected;

        if (this.isAllSelected) {
          this.selectedAuthors.push(this.authors[i]._source.name);
        }
      }
      this.updateAuthors(this.selectedAuthors);
    }
  }

  unselectAll() {
    for (let i = 0; i < this.authors.length; i++) {
      this.authors[i].selected = false;
    }
    this.selectedAuthors = [];
    this.updateAuthors(this.selectedAuthors);
  }

  toggleSelection(item: any, event: any) {
    //Needed for mat-option and mat-checkbox
    if (event.isUserInput || event.source._currentCheckState) {
      item.selected = !item.selected;
      if (item.selected && !this.isSelected(item)) {
        this.selectedAuthors.push(item._source.name);
      } else {
        const i = this.selectedAuthors.findIndex(
          (value) => value === item._source.name
        );
        this.selectedAuthors.splice(i, 1);
      }
      this.updateAuthors(this.selectedAuthors);
    }
  }

  updateAuthors(authorList: any[]) {
    var authors = [];
    for (var i = 0; i < authorList.length; i++) {
      authors.push(authorList[i]);
    }
    this.author.setValue(authors);
  }
  //Auto-Complete

  get author() {
    return this.f.get("author");
  }

  get tempAuthor() {
    return this.f.get("tempAuthor");
  }
}
